import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download,
  Loader2, Package, RefreshCw, Search, X,
} from 'lucide-react';

/**
 * Kampanyadan hangi ÜRÜN satıldı — CANLI GA4, marka bağımsız.
 *
 * Backend: /api/campaign-products/{overview,campaign,product,search}
 * Marka `localStorage.currentBrandId` üzerinden geliyor.
 *
 * 🔑 Atıf OTURUMUN kampanyasından: ürün, satın alındığı oturumun
 * `sessionCampaignName` değerine yazılır. Bu GA4'ün veri odaklı atıf modeli
 * DEĞİLDİR — son tıklama mantığıyla çalışır ve mecra panellerindeki
 * "dönüşüm değeri" ile birebir tutmayabilir. Şerit bunu yazıyor.
 *
 * 🔴 (direct) / (organic) / (referral) / (not set) kampanya değildir; GA4
 * kampanyasız oturumlara da bu alanı doldurur. Ayrı blokta, soluk.
 *
 * 🔑 Ürün markası süzgeci: "hangi kampanya kaç tane Playmobil sattı" sorusunun
 * cevabı. Süzgeç açıkken ciro/adet sütunları SEÇİLİ MARKAYA aittir; oturum ve
 * sipariş sütunları kampanyanın TAMAMINA ait kalır (oturum marka taşımaz).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Math.round(Number(n) || 0).toLocaleString('tr-TR');
const tl = (n) => `${nf(n)} ₺`;
const pct = (v, d = 1) => (v === null || v === undefined || Number.isNaN(v)
  ? '—' : `%${(v * 100).toFixed(d).replace('.', ',')}`);
const pctOf = (a, b, d = 1) => (b > 0 ? pct(a / b, d) : '—');
const dTR = (s) => (s ? s.split('-').reverse().join('.') : '—');
const iso = (d) => d.toISOString().slice(0, 10);

/* Kanal rozeti — GA4'ün kendi varsayılan kanal grubu. */
const KANAL_TON = {
  'Paid Search': 'bg-blue-50 text-blue-700 border-blue-200',
  'Cross-network': 'bg-violet-50 text-violet-700 border-violet-200',
  'Paid Social': 'bg-pink-50 text-pink-700 border-pink-200',
  'Paid Shopping': 'bg-amber-50 text-amber-700 border-amber-200',
  'Display': 'bg-teal-50 text-teal-700 border-teal-200',
  'Email': 'bg-cyan-50 text-cyan-700 border-cyan-200',
  'Organic Search': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Direct: 'bg-slate-100 text-slate-600 border-slate-200',
  Referral: 'bg-slate-100 text-slate-600 border-slate-200',
  Unassigned: 'bg-slate-100 text-slate-500 border-slate-200',
};

function Rozet({ kanal }) {
  if (!kanal) return null;
  const ton = KANAL_TON[kanal] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`shrink-0 rounded border px-1.5 py-px font-mono text-[10.5px] ${ton}`}>{kanal}</span>
  );
}

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[12px] font-bold uppercase tracking-wider text-indigo-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? (
        <p className="mt-1.5 max-w-[88ch] text-[13.5px] leading-relaxed text-slate-500">{children}</p>
      ) : null}
    </div>
  );
}

function Bar({ value, max, tone = 'indigo' }) {
  const w = max > 0 ? Math.max(1.5, (value / max) * 100) : 0;
  const bg = { indigo: 'bg-indigo-500', slate: 'bg-slate-400', rose: 'bg-rose-400' }[tone];
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
      <div className={`h-full rounded-full ${bg}`} style={{ width: `${w}%` }} />
    </div>
  );
}

/* ── Güven şeridi ─────────────────────────────────────────────────────────── */
function KapsamaSeridi({ toplam, meta, marka }) {
  const k = toplam.kapsama;
  const iyi = marka ? true : (k !== null && k >= 0.95);
  if (marka) {
    return (
      <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-indigo-700" />
          <div className="min-w-0">
            <p className="text-[13.5px] font-bold text-indigo-900">
              {marka.ad}: dönemde {nf(marka.adet)} adet, {tl(marka.ciro)}.
            </p>
            <p className="mt-1 max-w-[88ch] text-[13px] leading-relaxed text-indigo-800">
              Aşağıdaki tabloda <b>ciro ve adet yalnız {marka.ad} ürünlerine</b> aittir; kampanya×ürün
              markası kırılımından doğrudan okunuyor.{' '}
              <b>Oturum ve sipariş sütunları kampanyanın tamamına aittir</b> — bir oturum tek markaya
              ait değildir, o yüzden markaya bölünemez. Kampanyalı satış{' '}
              <b>{nf(toplam.kampanyaAdet)} adet</b>, yani {pctOf(toplam.kampanyaAdet, marka.adet)}’i;
              kalanı kampanyasız trafikte.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5 font-mono text-[11px] text-slate-600">
              <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">GA4 {meta.propertyId}</span>
              <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">{nf(toplam.urunCesidi)} ürün çeşidi</span>
              <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">
                {meta.cached ? 'önbellekten' : `${nf(meta.fetchMs)} ms`}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className={`rounded-xl border p-4 ${iyi ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
      <div className="flex items-start gap-3">
        {iyi ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-700" />
          : <AlertTriangle size={18} className="mt-0.5 shrink-0 text-amber-700" />}
        <div className="min-w-0">
          <p className={`text-[13.5px] font-bold ${iyi ? 'text-emerald-900' : 'text-amber-900'}`}>
            Ürün kırılımı, sipariş cirosunun {pct(k)}’ini açıklıyor.
          </p>
          <p className={`mt-1 max-w-[88ch] text-[13px] leading-relaxed ${iyi ? 'text-emerald-800' : 'text-amber-800'}`}>
            Sipariş cirosu <b>{tl(toplam.siparisCiro)}</b>, ürün satırlarının toplamı{' '}
            <b>{tl(toplam.urunCiro)}</b>.{' '}
            {iyi ? (
              <>İki rakam örtüşüyor; aşağıdaki ürün tablosu kampanyanın cirosunun tamamına yakınını taşıyor.</>
            ) : (
              <>Aradaki fark kargo/vergi ile ürün verisi göndermeyen siparişlerden geliyor —
                ürün tablosu kampanya cirosunun <b>bir bölümünü</b> açıklıyor, tamamını değil.</>
            )}{' '}
            Atıf oturumun kampanyasından: ürün, satın alındığı oturumun kampanyasına yazılır.{' '}
            <b>Bu GA4’ün veri odaklı atıf modeli değildir</b>, mecra panellerindeki dönüşüm değeriyle
            birebir tutmayabilir.
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5 font-mono text-[11px] text-slate-600">
            <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">
              GA4 {meta.propertyId}
            </span>
            <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">
              matris {nf(meta.matrisSatiri)} satır
            </span>
            <span className="rounded border border-slate-200 bg-white px-1.5 py-0.5">
              {meta.cached ? 'önbellekten' : `${nf(meta.fetchMs)} ms`}
            </span>
            {meta.truncated ? (
              <span className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-amber-800">
                satır tavanına takıldı — uzun aralıkta toplamlar eksik olabilir
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Ürün tablosu (kampanya açılınca) ────────────────────────────────────── */
const SIRA = {
  ciro: { etiket: 'Ciro', f: (a, b) => b.ciro - a.ciro },
  adet: { etiket: 'Adet', f: (a, b) => b.adet - a.adet },
  goruntuleme: { etiket: 'Görüntüleme', f: (a, b) => b.goruntuleme - a.goruntuleme },
};

function UrunTablosu({ kampanya, urunler, onUrun }) {
  const [sira, setSira] = useState('ciro');
  const [satmayan, setSatmayan] = useState(false);
  const [hepsi, setHepsi] = useState(false);

  const secili = useMemo(() => {
    const l = urunler.filter((u) => (satmayan ? u.adet === 0 && u.goruntuleme > 0 : u.adet > 0));
    return [...l].sort(satmayan ? SIRA.goruntuleme.f : SIRA[sira].f);
  }, [urunler, sira, satmayan]);

  const gosterilen = hepsi ? secili : secili.slice(0, 25);
  const maxCiro = secili.reduce((m, u) => Math.max(m, satmayan ? u.goruntuleme : u.ciro), 0);

  const csv = () => {
    const head = ['Kampanya', 'Ürün', 'Ürün kimliği', 'Marka', 'Kategori', 'Ciro', 'Adet', 'Görüntüleme', 'Sepete ekleme'];
    const rows = secili.map((u) => [
      kampanya.ad, u.urun, u.itemId || '', u.marka || '', u.kategori || '',
      Math.round(u.ciro), u.adet, u.goruntuleme, u.sepet,
    ]);
    const body = [head, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\n');
    const url = URL.createObjectURL(new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${kampanya.ad.replace(/[^\w.-]+/g, '_')}_urunler.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-4">
      {/* kampanyanın özeti */}
      <div className="mb-3 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ['Satan ürün çeşidi', nf(kampanya.satanUrun), `${nf(kampanya.gorulenUrun)} ürün görüntülendi`],
          ['Cironun yarısı', `${nf(kampanya.yariUrun)} üründe`, `en çok satan ürün payı ${pct(kampanya.ilkPay)}`],
          ['Görüntülemeden adete', pct(kampanya.gorunumAdet, 2), `${nf(kampanya.goruntuleme)} ürün görüntülemesi`],
          ['Sepetten adete', pct(kampanya.sepetAdet), `${nf(kampanya.sepet)} sepete ekleme`],
          ['Satmayana giden ilgi', pct(kampanya.oluPay), `${nf(kampanya.oluUrun)} ürün hiç satmadı`],
        ].map(([b, v, alt]) => (
          <div key={b} className="bg-white px-3 py-2.5">
            <p className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400">{b}</p>
            <p className="mt-0.5 text-[17px] font-bold leading-none tabular-nums text-slate-900">{v}</p>
            <p className="mt-1 text-[11.5px] leading-snug text-slate-500">{alt}</p>
          </div>
        ))}
      </div>

      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[false, true].map((v) => (
              <button
                key={String(v)}
                type="button"
                onClick={() => setSatmayan(v)}
                className={`px-2.5 py-1 text-[12px] font-medium ${
                  satmayan === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {v ? 'İlgi gördü, satmadı' : 'Satılan ürünler'}
              </button>
            ))}
          </div>
          {!satmayan ? (
            <div className="flex overflow-hidden rounded-lg border border-slate-200">
              {Object.entries(SIRA).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setSira(k)}
                  className={`px-2.5 py-1 text-[12px] font-medium ${
                    sira === k ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {v.etiket}
                </button>
              ))}
            </div>
          ) : null}
          <span className="text-[12px] text-slate-500">{nf(secili.length)} ürün</span>
        </div>
        <button
          type="button"
          onClick={csv}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[12px] font-medium text-slate-600 hover:bg-slate-50"
        >
          <Download size={13} /> CSV
        </button>
      </div>

      {satmayan ? (
        <p className="mb-2 max-w-[88ch] text-[12.5px] leading-relaxed text-slate-500">
          Bu kampanyanın gönderdiği ilgiyi alıp hiç satmayan ürünler.{' '}
          <b className="text-slate-700">Tek başına kusur değil</b> — bir oturumda onlarca ürün gezilip
          bir tanesi alınır. Anlamlı olan üstteki <b>pay</b>: görüntülemenin {pct(kampanya.oluPay)}’i
          bu ürünlere gitti. Listenin başındaki ürünler, kampanyanın en çok trafik yollayıp
          karşılığını alamadığı raflar.
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-50 text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 font-medium">#</th>
                <th className="px-3 py-2 font-medium">Ürün</th>
                <th className="w-[15%] px-3 py-2 font-medium">&nbsp;</th>
                <th className="px-3 py-2 text-right font-medium">Ciro</th>
                <th className="px-3 py-2 text-right font-medium">Pay</th>
                <th className="px-3 py-2 text-right font-medium">Adet</th>
                <th className="px-3 py-2 text-right font-medium">Görüntüleme</th>
                <th className="px-3 py-2 text-right font-medium">Sepete</th>
                <th className="px-3 py-2 text-right font-medium">Gör.→adet</th>
              </tr>
            </thead>
            <tbody>
              {gosterilen.map((u, i) => (
                <tr key={`${u.urunKey}-${i}`} className="border-t border-slate-100 hover:bg-indigo-50/40">
                  <td className="px-3 py-1.5 font-mono text-[11.5px] tabular-nums text-slate-400">{i + 1}</td>
                  <td className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => onUrun(u)}
                      className="text-left font-medium text-slate-800 hover:text-indigo-700 hover:underline"
                    >
                      {u.urun}
                    </button>
                    <span className="ml-2 font-mono text-[11px] text-slate-400">
                      {[u.itemId, u.marka, u.kategori].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    <Bar
                      value={satmayan ? u.goruntuleme : u.ciro}
                      max={maxCiro}
                      tone={satmayan ? 'rose' : 'indigo'}
                    />
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-800">{tl(u.ciro)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-500">
                    {pctOf(u.ciro, kampanya.urunCiro)}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-800">{nf(u.adet)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">{nf(u.goruntuleme)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">{nf(u.sepet)}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-500">
                    {u.goruntuleme > 0 ? pct(u.adet / u.goruntuleme, 2) : '—'}
                  </td>
                </tr>
              ))}
              {!gosterilen.length ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-[13px] text-slate-400">Kayıt yok.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
        {secili.length > 25 ? (
          <button
            type="button"
            onClick={() => setHepsi((v) => !v)}
            className="w-full border-t border-slate-100 bg-slate-50 py-1.5 text-[12.5px] font-medium text-indigo-700 hover:bg-slate-100"
          >
            {hepsi ? 'Kısalt' : `Kalan ${nf(secili.length - 25)} ürünü göster`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/* ── Kampanya satırı ─────────────────────────────────────────────────────── */
function KampanyaSatiri({ c, max, acik, onToggle, detay, yukleniyor, onUrun }) {
  return (
    <>
      <tr
        className={`cursor-pointer border-t border-slate-100 ${acik ? 'bg-indigo-50/60' : 'hover:bg-slate-50'}`}
        onClick={onToggle}
      >
        <td className="px-3 py-2 align-top">
          {acik ? <ChevronDown size={15} className="text-indigo-600" /> : <ChevronRight size={15} className="text-slate-400" />}
        </td>
        <td className="px-3 py-2">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-medium text-slate-900">{c.gercekAd || c.ad}</span>
            <Rozet kanal={c.kanal} />
            {c.gercekAd ? (
              <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-1.5 py-px font-mono text-[10.5px] text-amber-700">
                {c.mecra} · GA4’te kimlik
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 font-mono text-[11px] text-slate-400">
            {c.gercekAd ? `${c.ad} · ` : ''}{c.kaynak}{c.kaynakSayisi > 1 ? ` +${c.kaynakSayisi - 1} kaynak` : ''}
          </p>
          {!acik && c.onizleme.length ? (
            <p className="mt-1 truncate text-[11.5px] text-slate-500">
              {c.onizleme.slice(0, 3).map((u) => u.urun).join(' · ')}
              {c.satanUrun > 3 ? ` · +${nf(c.satanUrun - 3)}` : ''}
            </p>
          ) : null}
        </td>
        <td className="w-[12%] px-3 py-2"><Bar value={c.urunCiro} max={max} /></td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-800">{tl(c.urunCiro)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{nf(c.adet)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{nf(c.satanUrun)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{pct(c.ilkPay)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{nf(c.oturum)}</td>
        <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-600">{nf(c.siparis)}</td>
      </tr>
      {acik ? (
        <tr>
          <td colSpan={9} className="p-0">
            {yukleniyor ? (
              <div className="flex items-center justify-center gap-2 border-t border-slate-200 bg-slate-50/60 py-8 text-slate-400">
                <Loader2 size={16} className="animate-spin" /> ürünler çekiliyor…
              </div>
            ) : detay ? (
              <UrunTablosu kampanya={detay.kampanya} urunler={detay.urunler} onUrun={onUrun} />
            ) : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

/* ── Ürün → kampanya çekmecesi ───────────────────────────────────────────── */
function UrunCekmecesi({ veri, yukleniyor, onKapat }) {
  if (!veri && !yukleniyor) return null;
  const u = veri?.urun;
  const max = veri?.kampanyalar?.[0]?.ciro || 1;
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/20" onClick={onKapat}>
      <div
        className="h-full w-full max-w-[620px] overflow-y-auto bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-wider text-indigo-700">Bu ürünü hangi kampanya sattı</p>
            <p className="mt-1 text-[18px] font-bold leading-snug text-slate-900">{u?.urun || '…'}</p>
            {u ? (
              <p className="mt-0.5 font-mono text-[11.5px] text-slate-400">
                {[u.itemId, u.marka, u.kategori].filter(Boolean).join(' · ') || '—'}
              </p>
            ) : null}
          </div>
          <button type="button" onClick={onKapat} className="rounded p-1 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        {yukleniyor ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : veri ? (
          <div className="px-5 py-4">
            <div className="mb-4 grid grid-cols-4 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200">
              {[['Ciro', tl(u.ciro)], ['Adet', nf(u.adet)], ['Görüntüleme', nf(u.goruntuleme)], ['Sepete', nf(u.sepet)]].map(([b, v]) => (
                <div key={b} className="bg-white px-3 py-2">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{b}</p>
                  <p className="mt-0.5 text-[15px] font-bold tabular-nums text-slate-900">{v}</p>
                </div>
              ))}
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-400">
                  <th className="pb-1.5 font-medium">Kampanya</th>
                  <th className="pb-1.5 font-medium">&nbsp;</th>
                  <th className="pb-1.5 text-right font-medium">Ciro</th>
                  <th className="pb-1.5 text-right font-medium">Pay</th>
                  <th className="pb-1.5 text-right font-medium">Adet</th>
                  <th className="pb-1.5 text-right font-medium">Görün.</th>
                </tr>
              </thead>
              <tbody>
                {veri.kampanyalar.filter((k) => k.adet > 0 || k.ciro > 0).map((k) => (
                  <tr key={k.key} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2">
                      <span className={k.kampanyaMi ? 'font-medium text-slate-800' : 'text-slate-400'}>{k.ad}</span>
                      {!k.kampanyaMi ? (
                        <span className="ml-1.5 font-mono text-[10px] text-slate-400">kampanya değil</span>
                      ) : null}
                    </td>
                    <td className="w-[22%] py-1.5 pr-2">
                      <Bar value={k.ciro} max={max} tone={k.kampanyaMi ? 'indigo' : 'slate'} />
                    </td>
                    <td className="py-1.5 text-right font-mono tabular-nums text-slate-800">{tl(k.ciro)}</td>
                    <td className="py-1.5 pl-2 text-right font-mono tabular-nums text-slate-500">{pctOf(k.ciro, u.ciro)}</td>
                    <td className="py-1.5 pl-2 text-right font-mono tabular-nums text-slate-700">{nf(k.adet)}</td>
                    <td className="py-1.5 pl-2 text-right font-mono tabular-nums text-slate-500">{nf(k.goruntuleme)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-[12px] leading-relaxed text-slate-400">
              Satır sayısı ürünün satıldığı oturumların kampanyasına göre; toplam,
              sayfanın üst tablosundaki aynı ürünün bütün satırlarının toplamıdır.
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ── Sayfa ────────────────────────────────────────────────────────────────── */
export default function KampanyaUrunSatis() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId],
  );

  const varsayilan = useMemo(() => {
    const end = new Date(); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - 29);
    return { start: iso(start), end: iso(end) };
  }, []);

  const [aralik, setAralik] = useState(varsayilan);
  const [marka, setMarka] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [acik, setAcik] = useState(null);          // açık kampanya anahtarı
  const [detay, setDetay] = useState({});          // key -> { kampanya, urunler }
  const [detayYuk, setDetayYuk] = useState(false);
  const [urunVeri, setUrunVeri] = useState(null);
  const [urunYuk, setUrunYuk] = useState(false);
  const [q, setQ] = useState('');
  const [sonuc, setSonuc] = useState([]);
  const [hepsi, setHepsi] = useState(false);
  const aramaRef = useRef(null);

  const qs = useCallback(
    (extra = '') => `start=${aralik.start}&end=${aralik.end}${marka ? `&marka=${encodeURIComponent(marka)}` : ''}${extra}`,
    [aralik, marka],
  );
  /* Marka listesi ve ürün araması markadan bağımsız — süzgeç onlara girmemeli. */
  const qsHam = useCallback(
    (extra = '') => `start=${aralik.start}&end=${aralik.end}${extra}`,
    [aralik],
  );

  const load = useCallback(async (refresh = false) => {
    if (!brandId) return;
    setLoading(true);
    setAcik(null);
    setDetay({});
    try {
      const res = await fetch(
        `${API_BASE}/api/campaign-products/overview?${qs(refresh ? '&refresh=1' : '')}`,
        { credentials: 'include', headers },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Tablo yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, qs]);

  useEffect(() => { load(); }, [load]);

  const kampanyaAc = useCallback(async (key) => {
    if (acik === key) { setAcik(null); return; }
    setAcik(key);
    if (detay[key]) return;
    setDetayYuk(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/campaign-products/campaign?${qs()}&key=${encodeURIComponent(key)}`,
        { credentials: 'include', headers },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Ürünler yüklenemedi');
      setDetay((d) => ({ ...d, [key]: json }));
    } catch (e) {
      toast.error(e.message);
      setAcik(null);
    } finally {
      setDetayYuk(false);
    }
  }, [acik, detay, headers, qs]);

  const urunAc = useCallback(async (key) => {
    setUrunYuk(true);
    setUrunVeri(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/campaign-products/product?${qsHam()}&key=${encodeURIComponent(key)}`,
        { credentials: 'include', headers },
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Ürün yüklenemedi');
      setUrunVeri(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUrunYuk(false);
    }
  }, [headers, qsHam]);

  // Ürün arama — yazarken 250 ms bekle.
  useEffect(() => {
    if (q.trim().length < 2) { setSonuc([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/campaign-products/search?${qsHam()}&q=${encodeURIComponent(q.trim())}`,
          { credentials: 'include', headers },
        );
        const json = await res.json();
        if (res.ok) setSonuc(json.urunler || []);
      } catch (e) { /* arama sessiz başarısız olur */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, headers, qsHam]);

  const kampanyalar = useMemo(() => (data?.kampanyalar || []).filter((c) => c.kampanyaMi), [data]);
  const digerleri = useMemo(() => (data?.kampanyalar || []).filter((c) => !c.kampanyaMi), [data]);
  const max = kampanyalar[0]?.urunCiro || 1;
  const gosterilen = hepsi ? kampanyalar : kampanyalar.slice(0, 30);

  const hizli = (gun) => {
    const end = new Date(); end.setDate(end.getDate() - 1);
    const start = new Date(end); start.setDate(start.getDate() - (gun - 1));
    setAralik({ start: iso(start), end: iso(end) });
  };
  const gunSayisi = Math.round(
    (new Date(aralik.end) - new Date(aralik.start)) / 86400000,
  ) + 1;

  if (!brandId) {
    return <div className="p-6 text-[14px] text-slate-500">Önce bir marka seçin.</div>;
  }

  const t = data?.toplam;

  return (
    <div className="mx-auto max-w-[1280px] px-4 pb-16 pt-5">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Kampanyadan hangi ürün satıldı
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            {data ? (
              <>
                {data.meta.brandName} · GA4 {data.meta.propertyId} · {dTR(data.meta.start)} – {dTR(data.meta.end)}
                {data.marka ? <> · <b className="text-indigo-700">yalnız {data.marka.ad}</b></> : null}
              </>
            ) : 'GA4’ten canlı çekiliyor…'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => hizli(d)}
                className={`px-3 py-1.5 text-[12.5px] font-medium ${
                  gunSayisi === d ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {d} gün
              </button>
            ))}
          </div>
          <select
            value={marka}
            onChange={(e) => setMarka(e.target.value)}
            className="max-w-[240px] rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-[12.5px] font-medium text-slate-700"
            title="Ürün markası süzgeci"
          >
            <option value="">Tüm ürün markaları</option>
            {(data?.markalar || []).map((m) => (
              <option key={m.key} value={m.key}>
                {m.ad} — {nf(m.adet)} adet
              </option>
            ))}
          </select>
          <input
            type="date"
            value={aralik.start}
            max={aralik.end}
            onChange={(e) => setAralik((a) => ({ ...a, start: e.target.value }))}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-700"
          />
          <input
            type="date"
            value={aralik.end}
            min={aralik.start}
            onChange={(e) => setAralik((a) => ({ ...a, end: e.target.value }))}
            className="rounded-lg border border-slate-200 px-2 py-1.5 text-[12.5px] text-slate-700"
          />
          <button
            type="button"
            onClick={() => load(true)}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[12.5px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Tazele
          </button>
        </div>
      </div>

      {!data && loading ? (
        <div className="flex h-64 items-center justify-center text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : null}

      {data ? (
        <div className="flex flex-col gap-10">
          <KapsamaSeridi toplam={t} meta={data.meta} marka={data.marka} />

          {/* 01 — kampanyalar */}
          <section>
            <SectionHead
              eyebrow="01 — Kampanyalar"
              title={data.marka ? `Hangi kampanya kaç tane ${data.marka.ad} sattı` : 'Hangi kampanya hangi ürünü sattı'}
            >
              {data.marka ? (
                <>
                  Tablo <b>adede göre</b> sıralı. {nf(data.marka.adet)} adet {data.marka.ad}’in{' '}
                  <b>{nf(t.kampanyaAdet)} adedi</b> ({pctOf(t.kampanyaAdet, data.marka.adet)}) adı olan bir
                  kampanyadan geldi; {nf(t.kampanyaSayisi)} kampanya en az bir adet sattı. Kampanya adına
                  tıklayınca o kampanyanın sattığı {data.marka.ad} ürünleri tek tek açılır.
                </>
              ) : (
                <>
                  Kampanya adına tıklayınca o kampanyadan gelen oturumlarda satılan ürünlerin tamamı açılır.
                  Toplam ürün cirosunun <b>{pctOf(t.kampanyaCiro, t.urunCiro)}</b>’i adı olan bir kampanyadan
                  geliyor ({nf(t.kampanyaSayisi)} kampanya); kalanı aşağıdaki kampanyasız blokta.
                  “İlk ürün payı”, kampanyanın en çok satan ürününün o kampanyanın cirosundaki ağırlığı —
                  düşük değer cironun tabana yayıldığını, yüksek değer tek ürüne bağımlılığı gösterir.
                </>
              )}
            </SectionHead>

            <div className="mb-4 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
              {(data.marka ? [
                [`Toplam ${data.marka.ad}`, `${nf(t.adet)} adet`, `${tl(t.urunCiro)} · tüm trafik`],
                ['Kampanyadan gelen', `${nf(t.kampanyaAdet)} adet`, `${tl(t.kampanyaCiro)} · toplamın ${pctOf(t.kampanyaAdet, t.adet)}’i`],
                ['Satan kampanya', nf(t.kampanyaSayisi), 'en az bir adet satmış kampanya'],
                ['Satan ürün çeşidi', nf(t.urunCesidi), `${data.marka.ad} altında`],
              ] : [
                ['Kampanyalı ürün cirosu', tl(t.kampanyaCiro), `toplam ürün cirosunun ${pctOf(t.kampanyaCiro, t.urunCiro)}’i`],
                ['Kampanya sayısı', nf(t.kampanyaSayisi), `${nf(t.kampanyaOturum)} oturum · ${nf(t.kampanyaSiparis)} sipariş`],
                ['Satan ürün çeşidi', nf(t.urunCesidi), `${nf(t.adet)} adet ürün satıldı`],
                ['Sipariş cirosu', tl(t.siparisCiro), `${nf(t.siparis)} sipariş · tüm trafik`],
              ]).map(([b, v, alt]) => (
                <div key={b} className="bg-white p-4">
                  <p className="font-mono text-[10.5px] uppercase tracking-wider text-slate-400">{b}</p>
                  <p className="mt-1 text-[24px] font-bold leading-none tabular-nums text-slate-900">{v}</p>
                  <p className="mt-1.5 text-[12.5px] leading-snug text-slate-500">{alt}</p>
                </div>
              ))}
            </div>

            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-500">
                      <th className="w-8 px-3 py-2" />
                      <th className="px-3 py-2 font-medium">Kampanya</th>
                      <th className="px-3 py-2 font-medium">&nbsp;</th>
                      <th className="px-3 py-2 text-right font-medium">{data.marka ? `${data.marka.ad} cirosu` : 'Ürün cirosu'}</th>
                      <th className="px-3 py-2 text-right font-medium">{data.marka ? `${data.marka.ad} adedi` : 'Adet'}</th>
                      <th className="px-3 py-2 text-right font-medium">Satan ürün</th>
                      <th className="px-3 py-2 text-right font-medium">İlk ürün payı</th>
                      <th className="px-3 py-2 text-right font-medium" title={data.marka ? 'Kampanyanın tamamı — markaya bölünemez' : undefined}>
                        Oturum{data.marka ? '*' : ''}
                      </th>
                      <th className="px-3 py-2 text-right font-medium" title={data.marka ? 'Kampanyanın tamamı — markaya bölünemez' : undefined}>
                        Sipariş{data.marka ? '*' : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {gosterilen.map((c) => (
                      <KampanyaSatiri
                        key={c.key}
                        c={c}
                        max={max}
                        acik={acik === c.key}
                        onToggle={() => kampanyaAc(c.key)}
                        detay={detay[c.key]}
                        yukleniyor={acik === c.key && detayYuk && !detay[c.key]}
                        onUrun={(u) => urunAc(u.urunKey)}
                      />
                    ))}
                    {!gosterilen.length ? (
                      <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">Bu aralıkta adı olan kampanya yok.</td></tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {kampanyalar.length > 30 ? (
                <button
                  type="button"
                  onClick={() => setHepsi((v) => !v)}
                  className="w-full border-t border-slate-100 bg-slate-50 py-2 text-[12.5px] font-medium text-indigo-700 hover:bg-slate-100"
                >
                  {hepsi ? 'Kısalt' : `Kalan ${nf(kampanyalar.length - 30)} kampanyayı göster`}
                </button>
              ) : null}
            </div>
          </section>

          {/* 02 — kampanyasız trafik */}
          {digerleri.length ? (
            <section>
              <SectionHead eyebrow="02 — Kampanyasız trafik" title="Kampanyaya yazılamayan ciro">
                GA4 kampanyası olmayan oturumlara da bu alanı doldurur:{' '}
                <code className="rounded bg-slate-100 px-1">(direct)</code>,{' '}
                <code className="rounded bg-slate-100 px-1">(organic)</code>,{' '}
                <code className="rounded bg-slate-100 px-1">(referral)</code>,{' '}
                <code className="rounded bg-slate-100 px-1">(not set)</code>.{' '}
                <b className="text-slate-700">Bunlar kampanya değildir</b> ve üstteki listeye
                karıştırılmadı. Ürün kırılımları yine de açılabiliyor — çerez izni vermeyen
                ziyaretçinin siparişi de burada göründüğü için bu blok reklam cirosunu da barındırıyor olabilir.
              </SectionHead>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="bg-slate-50 text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-500">
                      <th className="w-8 px-3 py-2" />
                      <th className="px-3 py-2 font-medium">Satır</th>
                      <th className="px-3 py-2 font-medium">&nbsp;</th>
                      <th className="px-3 py-2 text-right font-medium">{data.marka ? `${data.marka.ad} cirosu` : 'Ürün cirosu'}</th>
                      <th className="px-3 py-2 text-right font-medium">{data.marka ? `${data.marka.ad} adedi` : 'Adet'}</th>
                      <th className="px-3 py-2 text-right font-medium">Satan ürün</th>
                      <th className="px-3 py-2 text-right font-medium">İlk ürün payı</th>
                      <th className="px-3 py-2 text-right font-medium" title={data.marka ? 'Kampanyanın tamamı — markaya bölünemez' : undefined}>
                        Oturum{data.marka ? '*' : ''}
                      </th>
                      <th className="px-3 py-2 text-right font-medium" title={data.marka ? 'Kampanyanın tamamı — markaya bölünemez' : undefined}>
                        Sipariş{data.marka ? '*' : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {digerleri.map((c) => (
                      <KampanyaSatiri
                        key={c.key}
                        c={c}
                        max={digerleri[0]?.urunCiro || 1}
                        acik={acik === c.key}
                        onToggle={() => kampanyaAc(c.key)}
                        detay={detay[c.key]}
                        yukleniyor={acik === c.key && detayYuk && !detay[c.key]}
                        onUrun={(u) => urunAc(u.urunKey)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {/* 03 — ters yön */}
          <section>
            <SectionHead eyebrow="03 — Ters yön" title="Bir ürünü hangi kampanyalar satıyor">
              Ürün adı ya da ürün kimliğiyle arayın; satırın üstüne tıklayınca o ürünü satan
              bütün kampanyalar ciro sırasıyla açılır. Aynı çekmece, yukarıdaki kampanya
              tablolarında bir ürün adına tıkladığınızda da açılıyor.
            </SectionHead>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  ref={aramaRef}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ürün adı veya ürün kimliği…"
                  className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-[13.5px] text-slate-800 outline-none focus:border-indigo-400"
                />
              </div>
              {q.trim().length >= 2 ? (
                <div className="mt-3 overflow-hidden rounded-lg border border-slate-200">
                  {sonuc.length ? sonuc.map((u) => (
                    <button
                      key={u.key}
                      type="button"
                      onClick={() => urunAc(u.key)}
                      className="flex w-full items-center justify-between gap-3 border-b border-slate-100 px-3 py-2 text-left last:border-0 hover:bg-indigo-50/50"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-medium text-slate-800">{u.urun}</p>
                        <p className="font-mono text-[11px] text-slate-400">
                          {[u.itemId, u.marka].filter(Boolean).join(' · ') || '—'}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-4 font-mono text-[12px] tabular-nums text-slate-600">
                        <span>{tl(u.ciro)}</span>
                        <span className="text-slate-400">{nf(u.adet)} adet</span>
                        <span className="text-indigo-700">{nf(u.kampanyaSayisi)} kampanya</span>
                      </div>
                    </button>
                  )) : (
                    <p className="px-3 py-4 text-center text-[13px] text-slate-400">Eşleşen ürün yok.</p>
                  )}
                </div>
              ) : null}

              {/* en çok satanlar — arama yapılmadan da bir giriş noktası */}
              {q.trim().length < 2 ? (
                <div className="mt-4">
                  <p className="mb-2 font-mono text-[10.5px] uppercase tracking-wider text-slate-400">
                    Dönemin en çok satan ürünleri
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="text-left font-mono text-[10.5px] uppercase tracking-wider text-slate-400">
                          <th className="pb-1.5 font-medium">#</th>
                          <th className="pb-1.5 font-medium">Ürün</th>
                          <th className="pb-1.5 text-right font-medium">Ciro</th>
                          <th className="pb-1.5 text-right font-medium">Adet</th>
                          <th className="pb-1.5 text-right font-medium">Kampanya</th>
                          <th className="pb-1.5 pl-4 font-medium">En çok satan kampanya</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.enCokSatan || []).slice(0, 20).map((u, i) => (
                          <tr key={u.key} className="border-t border-slate-100 hover:bg-indigo-50/40">
                            <td className="py-1.5 font-mono text-[11.5px] tabular-nums text-slate-400">{i + 1}</td>
                            <td className="py-1.5">
                              <button
                                type="button"
                                onClick={() => urunAc(u.key)}
                                className="text-left font-medium text-slate-800 hover:text-indigo-700 hover:underline"
                              >
                                {u.urun}
                              </button>
                              <span className="ml-2 font-mono text-[11px] text-slate-400">
                                {[u.itemId, u.marka].filter(Boolean).join(' · ')}
                              </span>
                            </td>
                            <td className="py-1.5 text-right font-mono tabular-nums text-slate-800">{tl(u.ciro)}</td>
                            <td className="py-1.5 text-right font-mono tabular-nums text-slate-600">{nf(u.adet)}</td>
                            <td className="py-1.5 text-right font-mono tabular-nums text-slate-600">{nf(u.kampanyaSayisi)}</td>
                            <td className="max-w-[260px] truncate py-1.5 pl-4 font-mono text-[11.5px] text-slate-500">
                              {u.enIyiKampanya || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null}
            </div>
          </section>

          <p className="flex items-start gap-2 text-[12.5px] leading-relaxed text-slate-400">
            <Package size={14} className="mt-0.5 shrink-0" />
            <span>
              Kaynak GA4 Data API. Ürün satırındaki <b>adet</b> satılan ürün sayısıdır, sipariş sayısı
              değil — sipariş GA4’te olay kapsamlı olduğu için ürün kırılımında istenemiyor ve yalnız
              kampanya satırında gösteriliyor. Bugün bilerek dışarıda: gün dolmadan çekilen ciro
              sonraki tazelemede değişiyor.
            </span>
          </p>
        </div>
      ) : null}

      {urunVeri || urunYuk ? (
        <UrunCekmecesi veri={urunVeri} yukleniyor={urunYuk} onKapat={() => { setUrunVeri(null); setUrunYuk(false); }} />
      ) : null}
    </div>
  );
}
